import React, { useContext, useEffect, useState } from "react";
import "./LoginForm.css";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import NotificationService from "../../components/NotificationService/NotificationService";
import SignupWizard from "../../components/SignupWizard/SignupWizard";
import { BaseUrl } from "../../BaseUrl";
import axios from "axios";
import { Spinner } from "react-bootstrap";

const LoginForm = () => {
  const {
    setUserLoggedIn,
    setUser,
    setProfilePicture,
  } = useContext(UserContext);
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState("login");

  // Login form state
  const [loginData, setLoginData] = useState({ email: "", password: "" });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check for URL params to show signup tab
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "signup") {
      setActiveTab("signup");
    }
  }, [searchParams]);

  // Clear errors when form data changes
  useEffect(() => {
    if (error) setError("");
    if (Object.keys(fieldErrors).length > 0) setFieldErrors({});
  }, [loginData]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData({ ...loginData, [name]: value });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");

    if (!forgotPasswordEmail.trim()) {
      setError("Please enter your email address");
      return;
    }

    if (!validateEmail(forgotPasswordEmail.trim())) {
      setError("Please enter a valid email address");
      return;
    }

    setForgotPasswordLoading(true);

    try {
      const response = await axios.post(`${BaseUrl}/api/v1/auth/forgot-password`, {
        email: forgotPasswordEmail.trim()
      });

      if (response.data.success) {
        NotificationService.notifySuccess("Password reset email sent! Please check your inbox.");
        setShowForgotPassword(false);
        setForgotPasswordEmail("");
      } else {
        setError(response.data.message || "Failed to send reset email");
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      if (!error.response) {
        setError("Unable to reach the server. Please check your internet connection and try again.");
      } else if (error.response.status >= 500) {
        setError("Our servers are having trouble right now. Please try again in a few minutes.");
      } else {
        setError(error.response?.data?.message || "Failed to send reset email. Please try again later.");
      }
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});

    if (!loginData.email.trim() || !loginData.password.trim()) {
      setError("Please enter email and password");
      return;
    }

    if (!validateEmail(loginData.email.trim())) {
      setFieldErrors({ email: "Please enter a valid email address" });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${BaseUrl}/api/v1/auth/login`, {
        email: loginData.email.trim(),
        password: loginData.password
      });

      if (response.data && response.data.success) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("userData", JSON.stringify(response.data.user));

        setUser(response.data.user);
        setUserLoggedIn(true);
        if (response.data.user.profilePicture) {
          setProfilePicture(response.data.user.profilePicture);
        }

        NotificationService.notifySuccess(response.data.message || "Login successful!");

        // Soft reminder if email not verified
        if (response.data.emailVerified === false) {
          NotificationService.notifyWarning("Please verify your email address. Check your inbox for the verification link.");
        }

        navigate("/");
      } else {
        setError(response.data?.message || "Login failed. Please try again.");
      }
    } catch (error) {
      let errorMessage = "An error occurred during login. Please try again.";
      if (error.response) {
        const status = error.response.status;
        const serverMsg = error.response.data?.message;

        if (status === 401) {
          errorMessage = serverMsg || "Invalid email or password. Please double-check and try again.";
        } else if (status === 403) {
          errorMessage = serverMsg || "Your account has been deactivated. Please contact support.";
        } else if (status === 423) {
          errorMessage = serverMsg || "Account temporarily locked. Please try again later.";
        } else if (status >= 500) {
          errorMessage = "Our servers are having trouble right now. Please try again in a few minutes.";
        } else {
          errorMessage = serverMsg || errorMessage;
        }
      } else if (error.request) {
        errorMessage = "Unable to reach the server. Please check your internet connection and try again.";
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSignupComplete = async (formData) => {
    setError("");
    setLoading(true);

    try {
      // Step 1: Create account
      const response = await axios.post(`${BaseUrl}/api/v1/auth/signup`, {
        fullName: formData.fullName.trim(),
        email: formData.email.trim(),
        password: formData.password,
        newsletterConsent: formData.newsletterConsent || false,
      });

      if (response.data.success && response.data.token) {
        localStorage.setItem("token", response.data.token);

        // Format dances for the API
        const formattedDances = formData.dances.map(dance => {
          // Format the since date
          let sinceFormatted = "";
          if (dance.since) {
            const [year, month] = dance.since.split("-");
            const monthNames = ["January", "February", "March", "April", "May", "June",
                                "July", "August", "September", "October", "November", "December"];
            sinceFormatted = `${monthNames[parseInt(month) - 1]} ${year}`;
          }

          return {
            style: dance.style,
            years: sinceFormatted,
            since: dance.since,
            follow: dance.follow === 'not-interested' ? '' : dance.follow,
            lead: dance.lead === 'not-interested' ? '' : dance.lead
          };
        });

        // Step 2: Update profile with additional info
        const profileResponse = await axios.put(
          `${BaseUrl}/api/v1/auth/update-profile`,
          {
            city: formData.city || undefined,
            homeLocation: formData.homeLocation.formatted ? formData.homeLocation : undefined,
            gender: formData.gender || undefined,
            ethnicity: formData.ethnicity || undefined,
            dateOfBirth: formData.dateOfBirth || undefined,
            profilePicture: formData.profilePicture || undefined,
            dances: formattedDances.length > 0 ? formattedDances : undefined
          },
          {
            headers: { Authorization: `Bearer ${response.data.token}` }
          }
        );

        const userData = profileResponse.data.user || response.data.user;
        localStorage.setItem("userData", JSON.stringify(userData));

        setUser(userData);
        setUserLoggedIn(true);
        if (userData.profilePicture) {
          setProfilePicture(userData.profilePicture);
        }

        NotificationService.notifySuccess("Account created successfully!");
        navigate("/");
      } else {
        throw new Error(response.data.message || "Failed to create account");
      }
    } catch (error) {
      console.error("Signup error:", error);

      // Extract the most specific error message available
      let errorMessage = "Something went wrong. Please try again.";

      if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
        errorMessage = "Unable to connect to server. Please check your internet connection and try again.";
      } else if (error.response?.status === 409) {
        errorMessage = "This email is already registered. Please switch to Sign In to log in.";
      } else if (error.response?.status === 400 && error.response?.data?.message) {
        // Validation error from backend (password too short, invalid email, etc.)
        errorMessage = error.response.data.message;
      } else if (error.response?.status >= 500) {
        errorMessage = "Our servers are having trouble right now. Please try again in a few minutes.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message && error.message !== "Network Error") {
        errorMessage = error.message;
      }

      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="Login_backgorund">
        <div className="auth-card">
          {/* Decorative corners */}
          <div className="corner-bl"></div>
          <div className="corner-br"></div>
          <div className="handle handle-left"></div>
          <div className="handle handle-right"></div>
          <div className="handle handle-top"></div>
          <div className="handle handle-bottom"></div>

          {/* Decorative dots */}
          <div className="decorative-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>

          {/* Tabs */}
          <div className="auth-tabs">
            <button
              className={`auth-tab ${activeTab === "login" ? "active" : ""}`}
              onClick={() => { setActiveTab("login"); setError(""); }}
              type="button"
            >
              Sign In
            </button>
            <button
              className={`auth-tab ${activeTab === "signup" ? "active" : ""}`}
              onClick={() => { setActiveTab("signup"); setError(""); }}
              type="button"
            >
              Sign Up
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}

          {/* Login Tab */}
          <div className={`tab-content ${activeTab === "login" ? "active" : ""}`}>
            <form onSubmit={handleLogin}>
              <div className="mb-3">
                <input
                  type="email"
                  name="email"
                  className={`form-control ${fieldErrors.email ? "input-error" : ""}`}
                  placeholder="Email"
                  value={loginData.email}
                  onChange={handleLoginChange}
                  disabled={loading}
                  required
                />
                {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
              </div>
              <div className="mb-3">
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="form-control"
                    placeholder="Password"
                    value={loginData.password}
                    onChange={handleLoginChange}
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
              <button type="submit" className="btn-primary-auth" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <i className="bi bi-box-arrow-in-right"></i>
                    Sign In
                  </>
                )}
              </button>
            </form>

            <div className="login-options">
              <button
                type="button"
                className="forgot-password-btn"
                onClick={() => setShowForgotPassword(true)}
                disabled={loading}
              >
                Forgot Password?
              </button>
            </div>
          </div>

          {/* Signup Tab */}
          <div className={`tab-content signup-wizard-tab ${activeTab === "signup" ? "active" : ""}`}>
            <SignupWizard
              onComplete={handleSignupComplete}
              onCancel={() => setActiveTab("login")}
            />
          </div>

          <div className="text-center">
            <Link to="/blogs" className="back-link">
              <i className="bi bi-arrow-left"></i>
              Continue browsing without signing in
            </Link>
          </div>

          <p className="footer-text">
            By signing in, you agree to our community values of kindness, curiosity, respect, and awareness.
          </p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="forgot-password-modal" onClick={(e) => {
          if (e.target.className === "forgot-password-modal") {
            setShowForgotPassword(false);
            setForgotPasswordEmail("");
            setError("");
          }
        }}>
          <div className="forgot-password-content">
            <button
              className="modal-close-btn"
              onClick={() => {
                setShowForgotPassword(false);
                setForgotPasswordEmail("");
                setError("");
              }}
              aria-label="Close modal"
            >
              x
            </button>
            <h3>Reset Your Password</h3>
            <p>Enter your email address and we'll send you a link to reset your password.</p>

            <form onSubmit={handleForgotPassword}>
              <div className="mb-3">
                <input
                  type="email"
                  className="form-control"
                  placeholder="Enter your email address"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  disabled={forgotPasswordLoading}
                  required
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="forgot-password-buttons">
                <button
                  type="submit"
                  disabled={forgotPasswordLoading}
                  className="send-reset-btn"
                >
                  {forgotPasswordLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Sending...
                    </>
                  ) : (
                    "Send Reset Link"
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail("");
                    setError("");
                  }}
                  className="cancel-btn"
                  disabled={forgotPasswordLoading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default LoginForm;
